import React from 'react';
import { Box, Stack, Text } from 'braid-design-system';

interface SearchItem {
  id: string;
  itemCode?: string;
  name: string;
  category: string;
  orgPrice: number;
  basePrice: number;
  price: number;
  discount: number;
  itemGroup: string;
  sih: number;
  barcode?: string;
}

interface SearchItemsModalProps {
  isOpen: boolean;
  categories: string[];
  selectedCategory: string;
  searchTerm: string;
  results: SearchItem[];
  onClose: () => void;
  onCategoryChange: (category: string) => void;
  onSearchTermChange: (value: string) => void;
  onAddItem: (item: SearchItem) => void;
  onEnterKey?: (item: SearchItem) => void;
  hideCategories?: boolean;
}

const SearchItemsModal: React.FC<SearchItemsModalProps> = ({
  isOpen,
  categories,
  selectedCategory,
  searchTerm,
  results,
  onClose,
  onCategoryChange,
  onSearchTermChange,
  onAddItem,
  onEnterKey,
  hideCategories = false,
}) => {
  if (!isOpen) {
    return null;
  }

  const handleSearchInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && results.length > 0) {
      event.preventDefault();
      const firstItem = results[0];
      if (onEnterKey) {
        onEnterKey(firstItem);
      } else {
        onAddItem(firstItem);
      }
    }
  };

  const handleOverlayKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  const handleResultCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, item: SearchItem) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (onEnterKey) {
        onEnterKey(item);
      } else {
        onAddItem(item);
      }
    }
  };

  return (
    <div className="search-modal-overlay" onKeyDown={handleOverlayKeyDown}>
      <div className="search-modal">
        <div className="search-modal-header">
          <Text weight="strong" size="large">
            Search Items
          </Text>
          <button type="button" onClick={onClose} className="search-modal-close">
            ✕
          </button>
        </div>

        <div className="search-modal-content">
          <Stack space="medium">
            {!hideCategories && (
              <Stack space="small">
                <Text weight="strong" size="standard">
                  Category
                </Text>
                <div className="category-filters">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => onCategoryChange(category)}
                      className={`category-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </Stack>
            )}

            <Stack space="small">
              <Text weight="strong" size="standard">
                Search
              </Text>
              <input
                type="text"
                placeholder="Enter item name, code, or barcode..."
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                onKeyDown={handleSearchInputKeyDown}
                className="search-input"
                autoFocus
              />
            </Stack>
          </Stack>

          <div className="search-results-grid">
            {results.length > 0 ? (
              results.map((item) => (
                <div
                  key={item.id}
                  className="search-result-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => onAddItem(item)}
                  onKeyDown={(event) => handleResultCardKeyDown(event, item)}
                >
                  <div className="result-card-content">
                    <Text weight="strong" size="standard">
                      {item.name}
                    </Text>
                    <Text tone="secondary" size="small">
                      {item.itemCode || item.barcode || item.category} | Stock: {item.sih}
                    </Text>
                    <Box display="flex" justifyContent="spaceBetween" alignItems="center" paddingTop="small">
                      <Text weight="strong" size="large">
                        {item.price.toFixed(2)}
                      </Text>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onAddItem(item);
                        }}
                        className="add-to-cart-btn"
                        tabIndex={-1}
                      >
                        +
                      </button>
                    </Box>
                  </div>
                </div>
              ))
            ) : (
              <Text tone="secondary">No items found</Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchItemsModal;
